import { useEffect, useMemo, useState } from "react";
import { X, Eye, EyeOff } from "lucide-react";
import { type DebugAnalyticsEvent } from "@/lib/analytics";

/**
 * Per-mount diagnostics panel for the Wheelbase booking widget.
 *
 * Listens to `analytics:event` and groups everything by widget mount
 * (delimited by `booking_widget_view`). For each mount it surfaces:
 *   - the most recent vehicle_selected, pickup_date, return_date
 *   - matched_strategy from the latest booking_widget_* event
 *   - the handoff destination_host + trigger (once detected)
 *
 * Toggle via `?debug=widget` (persists in localStorage). Hide via `?debug=off`.
 *
 * Stays out of the way for normal users — only renders when explicitly opted in.
 */

const STORAGE_KEY = "widget_diagnostics_enabled";

function isEnabled(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debug") === "widget") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (url.searchParams.get("debug") === "off") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

interface MountSnapshot {
  id: number;
  startedAt: number;
  vehicle_selected?: string;
  pickup_date?: string;
  return_date?: string;
  matched_strategy?: string;
  lastEvent?: string;
  lastEventAt?: number;
  handoff?: {
    destination_host?: string;
    destination_url?: string;
    trigger?: string;
    ms_since_search_click?: number;
    firedAt: number;
  };
  searchClicks: number;
  errored: boolean;
}

const WIDGET_EVENT_RE = /^booking_widget_/;

const formatTime = (ts: number) => {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const pad = (n: number) => n.toString().padStart(2, "0");

export default function WidgetDiagnosticsPanel() {
  const [enabled, setEnabled] = useState(false);
  const [mounts, setMounts] = useState<MountSnapshot[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setEnabled(isEnabled());
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<DebugAnalyticsEvent>).detail;
      if (!detail || !WIDGET_EVENT_RE.test(detail.name)) return;
      const params = (detail.params || {}) as Record<string, unknown>;

      setMounts((prev) => {
        const next = [...prev];
        // booking_widget_view marks a fresh mount.
        if (detail.name === "booking_widget_view") {
          next.push({
            id: (next[next.length - 1]?.id ?? 0) + 1,
            startedAt: detail.timestamp,
            searchClicks: 0,
            errored: false,
            lastEvent: detail.name,
            lastEventAt: detail.timestamp,
          });
          // Cap history to last 8 mounts
          while (next.length > 8) next.shift();
          return next;
        }

        // Update most recent mount; if none yet, seed one (page loaded mid-session).
        let cur = next[next.length - 1];
        if (!cur) {
          cur = {
            id: 1,
            startedAt: detail.timestamp,
            searchClicks: 0,
            errored: false,
          };
          next.push(cur);
        }

        const v = pickStr(params.vehicle_selected);
        const p = pickStr(params.pickup_date);
        const r = pickStr(params.return_date);
        const m = pickStr(params.matched_strategy);
        if (v) cur.vehicle_selected = v;
        if (p) cur.pickup_date = p;
        if (r) cur.return_date = r;
        if (m) cur.matched_strategy = m;
        cur.lastEvent = detail.name;
        cur.lastEventAt = detail.timestamp;

        if (detail.name === "booking_widget_search_click") cur.searchClicks += 1;
        if (detail.name === "booking_widget_error") cur.errored = true;

        if (detail.name === "booking_widget_handoff") {
          cur.handoff = {
            destination_host: pickStr(params.destination_host),
            destination_url: pickStr(params.destination_url),
            trigger: pickStr(params.trigger),
            ms_since_search_click:
              typeof params.ms_since_search_click === "number"
                ? (params.ms_since_search_click as number)
                : undefined,
            firedAt: detail.timestamp,
          };
        }

        // Replace last entry with mutated copy so React re-renders.
        next[next.length - 1] = { ...cur };
        return next;
      });
    };

    window.addEventListener("analytics:event", handler);
    return () => window.removeEventListener("analytics:event", handler);
  }, [enabled]);

  const ordered = useMemo(() => [...mounts].reverse(), [mounts]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[9999] max-w-sm w-[22rem] font-mono text-xs">
      <div className="rounded-lg border border-border bg-background/95 backdrop-blur shadow-xl overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="font-semibold text-foreground">Widget diagnostics</span>
            <span className="text-muted-foreground">({mounts.length} mount{mounts.length === 1 ? "" : "s"})</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setEnabled(false);
              }}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Hide panel"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {ordered.length === 0 && (
              <div className="px-3 py-4 text-muted-foreground">
                No widget events yet. Visit the homepage and interact with the booking widget.
              </div>
            )}
            {ordered.map((m, idx) => {
              const isCurrent = idx === 0;
              return (
                <div key={m.id} className="px-3 py-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">
                        mount #{m.id}
                      </span>
                      {isCurrent && (
                        <span className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                          current
                        </span>
                      )}
                      {m.errored && (
                        <span className="rounded-full border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[10px] text-destructive">
                          error
                        </span>
                      )}
                    </div>
                    <span className="text-muted-foreground">{formatTime(m.startedAt)}</span>
                  </div>

                  <Row label="vehicle" value={m.vehicle_selected} />
                  <Row label="pickup" value={m.pickup_date} />
                  <Row label="return" value={m.return_date} />
                  <Row
                    label="matched"
                    value={m.matched_strategy}
                    title={m.matched_strategy}
                  />
                  <Row
                    label="last evt"
                    value={
                      m.lastEvent
                        ? `${m.lastEvent.replace(/^booking_widget_/, "")}${
                            m.lastEventAt ? ` · ${formatTime(m.lastEventAt)}` : ""
                          }`
                        : undefined
                    }
                  />
                  <Row
                    label="search clicks"
                    value={m.searchClicks > 0 ? String(m.searchClicks) : undefined}
                  />

                  <div className="pt-1">
                    {m.handoff ? (
                      <div className="rounded border border-primary/40 bg-primary/5 p-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-primary">handoff fired</span>
                          <span className="text-muted-foreground">
                            {formatTime(m.handoff.firedAt)}
                          </span>
                        </div>
                        <Row label="trigger" value={m.handoff.trigger} compact />
                        <Row label="host" value={m.handoff.destination_host} compact />
                        {m.handoff.destination_url && (
                          <div
                            className="break-all text-muted-foreground"
                            title={m.handoff.destination_url}
                          >
                            <span className="text-foreground/70">url:</span>{" "}
                            {truncate(m.handoff.destination_url, 90)}
                          </div>
                        )}
                        {m.handoff.ms_since_search_click !== undefined && (
                          <Row
                            label="Δ search→handoff"
                            value={`${m.handoff.ms_since_search_click} ms`}
                            compact
                          />
                        )}
                      </div>
                    ) : (
                      <div className="rounded border border-dashed border-border bg-muted/30 p-2 text-muted-foreground">
                        no handoff detected
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t border-border bg-muted/30 px-3 py-1.5 text-[10px] text-muted-foreground">
          toggle with <code>?debug=widget</code> · hide with <code>?debug=off</code>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  compact,
  title,
}: {
  label: string;
  value?: string;
  compact?: boolean;
  title?: string;
}) {
  return (
    <div className={`flex items-baseline gap-2 ${compact ? "" : ""}`} title={title}>
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span
        className={`flex-1 truncate ${value ? "text-foreground" : "text-muted-foreground/50"}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function pickStr(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed ? trimmed : undefined;
}

function truncate(s: string, n: number) {
  return s.length <= n ? s : `${s.slice(0, n)}…`;
}
